import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import {
  BudgetItemSummary,
  BudgetMacroItem,
  CashFund,
  CashFundOrigin,
  CashFundSplitRow,
  CashFundsService,
  CostCenterSummary,
  Identity,
  ProjectArticle,
  ProjectSubtaskSummary,
} from '@libs/core/api';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoadingService, ToastService } from '@libs/ui/uikit';
import { AlertController, ModalController } from '@ionic/angular';
import {
  BehaviorSubject,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { FormBuilder, Validators } from '@angular/forms';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { OverlayEventDetail } from '@ionic/core';
import {
  Attachment,
  compactAttachments,
  getAttachmentAt,
  normalizeOtherAttachments,
  shouldShowAttachmentSlot,
  toOtherAttachmentSlotPatch,
  toOtherFilesRequest,
} from '../../fund-cash-attachments.utils';
import { FundCashSplitListModalComponent } from '../fund-cash-split-list-modal/fund-cash-split-list-modal.component';

enum PurchaseDocumentType {
  RECEIPT = 'receipt',
  INVOICE = 'invoice',
}

@UntilDestroy()
@Component({
  selector: 'front-end-fund-cash-purchase-send-modal',
  templateUrl: './fund-cash-purchase-send-modal.component.html',
  styleUrls: ['./fund-cash-purchase-send-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundCashPurchaseSendModalComponent implements OnInit {
  @Input() id?: number;
  @Input() project!: CostCenterSummary;
  @Input() identity!: Identity;
  @Input() cashFund?: CashFund;

  isDraft = false;
  splitRows: CashFundSplitRow[] = [];

  budgetProjectTasks: BudgetMacroItem[] = [];
  allowedSubtasks: ProjectSubtaskSummary[] = [];
  allowedSuperbuyerSubtasks: ProjectSubtaskSummary[] = [];
  hasBudgetItem = false;

  cashFund$ = new BehaviorSubject<CashFund | null>(null);

  isPaid$ = this.cashFund$.pipe(map((cashFund) => cashFund?.isPaid ?? false));
  readonly PurchaseDocumentType = PurchaseDocumentType;

  form = this.fb.group({
    article: this.fb.control<ProjectArticle | null | undefined>(undefined, [
      Validators.required,
    ]),
    budgetItem: this.fb.control<BudgetItemSummary | undefined>(undefined, [
      Validators.required,
    ]),
    purchaseDocumentType: this.fb.control<PurchaseDocumentType>(
      PurchaseDocumentType.RECEIPT
    ),
    purchaseDocument: this.fb.control<Attachment | null>(null, [
      Validators.required,
    ]),
    otherAttachment1: this.fb.control<Attachment | null>(null),
    otherAttachment2: this.fb.control<Attachment | null>(null),
    otherAttachment3: this.fb.control<Attachment | null>(null),
    otherAttachment4: this.fb.control<Attachment | null>(null),
    notes: this.fb.control<string | null>(null),
  });

  constructor(
    private readonly modalController: ModalController,
    private readonly alertController: AlertController,
    private readonly translateService: TranslateService,
    private readonly cashFundService: CashFundsService,
    private readonly toastService: ToastService,
    private readonly loadingService: LoadingService,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (!this.id) return;

    if (this.cashFund) {
      this.splitRows = [...(this.cashFund.splitRows ?? [])];
    }

    this.budgetProjectTasks =
      this.identity.companies?.flatMap(
        (c) => c.costCenters?.flatMap((cc) => cc.macroItems ?? []) ?? []
      ) ?? [];

    this.allowedSubtasks =
      this.project.subtasks?.filter(
        (subtask) => (subtask.buyer ?? false) || (subtask.superBuyer ?? false)
      ) ?? [];

    this.allowedSuperbuyerSubtasks =
      this.project.subtasks?.filter((subtask) => subtask.superBuyer ?? false) ??
      [];

    this.cashFundService
      .getCashFund({ cashFundId: this.id })
      .pipe(
        tap((cashFund) => {
          this.cashFund$.next(cashFund);

          const receipt =
            cashFund.receiptFileUrl && cashFund.receiptFileName
              ? {
                  name: cashFund.receiptFileName,
                  url: cashFund.receiptFileUrl,
                }
              : null;

          const invoice =
            cashFund.invoiceFileUrl && cashFund.invoiceFileName
              ? {
                  name: cashFund.invoiceFileName,
                  url: cashFund.invoiceFileUrl,
                }
              : null;

          this.form.patchValue({
            notes: cashFund.comment,
            purchaseDocumentType: invoice
              ? PurchaseDocumentType.INVOICE
              : PurchaseDocumentType.RECEIPT,
            purchaseDocument: invoice ?? receipt,
            budgetItem: cashFund.subtask,
            article: cashFund.article,
          });

          this.hasBudgetItem =
            cashFund.article != null && cashFund.subtask != null;

          this.patchOtherAttachments(cashFund.otherFiles);
          this.cdr.markForCheck();
        }),
        this.toastService.catchErrorWithToast(),
        this.loadingService.withLoading(),
        untilDestroyed(this)
      )
      .subscribe();
  }

  get showBudgetItemControl(): boolean {
    return (
      this.cashFund?.origin === CashFundOrigin.Whitelist && !this.hasBudgetItem
    );
  }

  get totalAmount(): number {
    return this.cashFund?.authorizedAmount ?? 0;
  }

  get totalSplitAmount(): number {
    return this.splitRows.reduce((sum, row) => sum + (row.amount ?? 0), 0);
  }

  get remainingAmount(): number {
    return this.totalAmount - this.totalSplitAmount;
  }

  setIsDraft(): void {
    this.isDraft = true;
  }

  get purchaseDocumentType(): PurchaseDocumentType {
    return (
      this.form.controls.purchaseDocumentType.value ??
      PurchaseDocumentType.RECEIPT
    );
  }

  get purchaseDocumentAttachment(): Attachment | null {
    return this.form.value.purchaseDocument ?? null;
  }

  async openSplitModal(): Promise<void> {
    const cashFund = this.cashFund$.value;
    if (!cashFund) return;

    const modal = await this.modalController.create({
      component: FundCashSplitListModalComponent,
      componentProps: {
        cashFund: { ...cashFund, splitRows: this.splitRows },
        project: this.project,
        identity: this.identity,
      },
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss<CashFundSplitRow[]>();

    if (role === 'confirm' && data) {
      this.splitRows = data;
      this.cdr.markForCheck();
    }
  }

  onPurchaseDocumentTypeChange(event: Event): void {
    const documentType = (event as CustomEvent).detail.value as
      | PurchaseDocumentType
      | undefined;

    if (!documentType || documentType === this.purchaseDocumentType) return;

    this.form.patchValue({
      purchaseDocumentType: documentType,
    });
  }

  onAttachPurchaseDocument(attachment: Attachment | null): void {
    this.form.patchValue({
      purchaseDocument: attachment,
    });
  }

  onAttachOtherAttachment(index: number, attachment: Attachment | null): void {
    const attachments = [...this.getOtherAttachments()];

    if (attachment === null) {
      attachments.splice(index, 1);
      this.patchOtherAttachments(attachments);
      return;
    }

    if (index >= attachments.length) {
      attachments.push(attachment);
      this.patchOtherAttachments(attachments);
      return;
    }

    attachments[index] = attachment;
    this.patchOtherAttachments(attachments);
  }

  getOtherAttachment(index: number): Attachment | null {
    return getAttachmentAt(this.getOtherAttachmentSlots(), index);
  }

  shouldShowOtherAttachment(index: number): boolean {
    return shouldShowAttachmentSlot(this.getOtherAttachmentSlots(), index);
  }

  save(): void {
    if (!this.id) return;
    if (!this.isDraft && !this.form.valid) return;

    const purchaseDocument = this.purchaseDocumentAttachment;
    const isReceipt =
      this.purchaseDocumentType === PurchaseDocumentType.RECEIPT;
    const isInvoice =
      this.purchaseDocumentType === PurchaseDocumentType.INVOICE;

    const request = {
      comment: this.form.value.notes ?? undefined,
      receiptFileName: isReceipt ? purchaseDocument?.name ?? null : null,
      receiptFileUrl: isReceipt ? purchaseDocument?.url ?? null : null,
      invoiceFileName: isInvoice ? purchaseDocument?.name ?? null : null,
      invoiceFileUrl: isInvoice ? purchaseDocument?.url ?? null : null,
      otherFiles: this.getOtherFilesForRequest(),
      articleId: this.form.controls.article.value?.id,
      subtaskId: this.form.controls.budgetItem.value?.id,
      splitRows:
        this.splitRows.length > 0
          ? this.splitRows.map((r) => ({
              subtaskId: r.subtask?.id,
              articleId: r.article?.id,
              amount: r.amount,
            }))
          : undefined,
    };

    const action$ = this.isDraft
      ? this.cashFundService.savePaidCashFundDraft({
          cashFundId: this.id,
          body: request,
        })
      : this.cashFundService.confirmCashFundPurchase({
          cashFundId: this.id,
          body: request,
        });

    this.confirmAlert()
      .pipe(
        switchMap(() => action$.pipe(this.loadingService.withLoading())),
        tap(() => {
          if (this.isDraft) {
            this.toastService.success(
              'pages.fund-cash.purchase.saveDraft.confirmation.message'
            );
            this.modalController.dismiss('draft');
            return;
          }
          this.toastService.success('pages.fund-cash.purchase.confirm.success');
          this.modalController.dismiss('finalized');
        }),
        this.toastService.catchErrorWithToast(),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private confirmAlert(): Observable<OverlayEventDetail<void> | void> {
    const alertObserver$ = from(
      this.alertController.create({
        header: this.translateService.instant(
          'pages.fund-cash.purchase.confirm.noInvoiceAlert.header'
        ),
        message: this.translateService.instant(
          'pages.fund-cash.purchase.confirm.noInvoiceAlert.message'
        ),
        buttons: [
          {
            text: this.translateService.instant(
              'pages.fund-cash.purchase.confirm.noInvoiceAlert.cancel'
            ),
            role: 'cancel',
          },
          {
            text: this.translateService.instant(
              'pages.fund-cash.purchase.confirm.noInvoiceAlert.confirm'
            ),
            role: 'confirm',
          },
        ],
      })
    ).pipe(
      switchMap((alert) => from(alert.present()).pipe(map(() => alert))),
      switchMap((alert) => from(alert.onWillDismiss())),
      filter(({ role }) => role === 'confirm')
    );

    return !this.isDraft &&
      this.purchaseDocumentType !== PurchaseDocumentType.INVOICE
      ? alertObserver$
      : of(void 0);
  }

  private patchOtherAttachments(
    otherFiles:
      | Attachment[]
      | Array<{ fileName?: null | string; fileUrl?: null | string }>
      | undefined
  ): void {
    this.form.patchValue(
      toOtherAttachmentSlotPatch(normalizeOtherAttachments(otherFiles))
    );
  }

  private getOtherAttachments(): Attachment[] {
    return compactAttachments(this.getOtherAttachmentSlots());
  }

  private getOtherFilesForRequest(): Array<{
    fileName: string;
    fileUrl: string;
  }> {
    return toOtherFilesRequest(this.getOtherAttachmentSlots());
  }

  private getOtherAttachmentSlots(): Array<Attachment | null | undefined> {
    return [
      this.form.value.otherAttachment1,
      this.form.value.otherAttachment2,
      this.form.value.otherAttachment3,
      this.form.value.otherAttachment4,
    ];
  }
}
